import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import styles from "./CommentSection.module.css";
import profilePic from "../../assets/profile.png";

// Your backend path
const API_BASE_URL =
  "https://weatherbuddy.org/HomeMate/backend/"; //update to use global BASE_URL

// =======================================================
// USER TAG UTILS
// =======================================================

// [USER_ID:123]
const USER_ID_TAG_REGEX = /\[USER_ID:(\d+)\]/g;

// Convert @username → [USER_ID:X]
async function convertMentionsToUserIds(text) {
  const matches = text.match(/@([A-Za-z0-9_]+)/g);
  if (!matches) return text;

  let finalText = text;

  for (const match of matches) {
    const username = match.substring(1);
    const res = await fetch(`${API_BASE_URL}search_users.php?q=@${username}`);
    const data = await res.json();

    if (data && data.length > 0) {
      const userId = data[0].id;
      finalText = finalText.replace(match, `[USER_ID:${userId}]`);
    }
  }

  return finalText;
}

// Convert [USER_ID:x] → clickable @username link
const parseCommentText = (text, userMap) => {
  if (!text) return [];
  let parts = [];
  let lastIndex = 0;
  let keyIndex = 0;

  text.replace(USER_ID_TAG_REGEX, (match, userId, offset) => {
    if (offset > lastIndex) {
      parts.push(text.substring(lastIndex, offset));
    }

    // UPDATED: Use the map, or fallback gracefully, but do not assume 'userX'
    const displayName = userMap[userId] || `unknown`; //unkown should not be possible on new version

    parts.push(
      <Link
        key={keyIndex++}
        to={`/profile/${userId}`}
        className={`${styles.commentProfileLink} ${styles.underline}`}
      >
        @{displayName}
      </Link>
    );

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
};

// =======================================================
// Comment Item (recursive)
// =======================================================
function CommentItem({
  comment,
  depth,
  onAddReply,
  userMap,
  highlightedCommentId,
  isClosed,
}) {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState("");

  const MAX_DEPTH = 5;
  const INDENT = 25;

  let marginLeft = "0px";
  let borderLeft = "none";
  let paddingLeft = "12px";

  if (depth > 0) {
    const currentDepth = Math.min(depth, MAX_DEPTH);
    const parentDepth = Math.min(depth - 1, MAX_DEPTH);

    const extraLevels = currentDepth - parentDepth;
    marginLeft = `${INDENT * extraLevels}px`;

    if (depth <= MAX_DEPTH) {
      borderLeft = "2px solid #e5e5e5";
      paddingLeft = "12px";
    } else {
      borderLeft = "none";
      paddingLeft = "0px";
      marginLeft = "0px";
    }
  }

  const profileSrc = comment.user.avatarPath || profilePic;

  // Pass the userMap to the parser
  const parsed = parseCommentText(comment.text, userMap);

  const isHighlighted = String(comment.id) === highlightedCommentId;

  return (
    <div
      className={styles.replyBlock}
      style={{ marginLeft, paddingLeft, borderLeft }}
    >
      <div
        className={`${styles.commentCard} ${
          isHighlighted ? styles.highlighted : ""
        }`}
      >
        <Link to={`/profile/${comment.user.id}`}>
          <img src={profileSrc} className={styles.avatar} alt="Profile" />
        </Link>

        <div className={styles.commentContent}>
          <div className={styles.commentUser}>
            @{comment.user.username || "unknown"}
          </div>

          <div className={styles.commentText}>{parsed}</div>

          <button
            className={styles.replyBtn}
            onClick={() => setShowReplyBox(!showReplyBox)}
            disabled={isClosed}
          >
            Reply
          </button>

          {showReplyBox && !isClosed && (
            <div className={styles.replyInputRow}>
              <input
                className={styles.replyInput}
                value={replyText}
                placeholder="Write a reply..."
                onChange={(e) => setReplyText(e.target.value)}
              />
              <button
                className={styles.postBtn}
                onClick={() => {
                  if (!replyText.trim()) return;
                  onAddReply(comment.id, replyText);
                  setReplyText("");
                  setShowReplyBox(false);
                }}
              >
                Post
              </button>
            </div>
          )}
        </div>
      </div>

      {comment.replies.map((reply) => (
        <CommentItem
          key={reply.id}
          comment={reply}
          depth={depth + 1}
          onAddReply={onAddReply}
          userMap={userMap}
          highlightedCommentId={highlightedCommentId}
          isClosed={isClosed}
        />
      ))}
    </div>
  );
}

// =======================================================
// MAIN COMPONENT
// =======================================================
export default function CommentSection({ isClosed = false }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [userMap, setUserMap] = useState({});
  const [currentUserId, setCurrentUserId] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const { id } = useParams();
  const POST_ID = id;

  const location = useLocation();
  const navigate = useNavigate();

  const highlightedCommentId = new URLSearchParams(location.search).get(
    "commentId"
  );

  useEffect(() => {
    if (errorMsg) {
      const timer = setTimeout(() => {
        setErrorMsg("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMsg]);

  // UPDATED: Fetch comments AND the map of mentioned users in one go
  const fetchComments = useCallback(() => {
    if (!POST_ID) return;

    fetch(`${API_BASE_URL}get_comments.php?post_id=${POST_ID}`)
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load comments");
        return r.json();
      })
      .then((data) => {
        // Handle legacy array response just in case, or new object response
        if (Array.isArray(data)) {
            setComments(data);
        } else {
            setComments(data.comments || []);
            // Merge the new map from backend with any existing map
            setUserMap(prev => ({ ...prev, ...data.userMap }));
        }
      })
      .catch((err) => {
        console.error("Comment Fetch Error:", err);
        setErrorMsg("Unable to retrieve comments. Please try again later.");
      });
  }, [POST_ID]);

  // Initial load
  useEffect(() => {
    fetch(`${API_BASE_URL}get_user_info.php`)
      .then((r) => r.json())
      .then((d) => setCurrentUserId(d.user_id || 0))
      .catch(() => {}); 

    fetchComments();
  }, [fetchComments]);

  // NOTE: Removed the useEffect that called `get_usernames_by_id.php`. 
  // It is no longer needed because get_comments.php returns the data.

  // Post comment
  const postCommentToBackend = (parentId, text) => {
    if (!text.trim() || !POST_ID || currentUserId === 0) {
      setErrorMsg("You must be logged in to post a comment.");
      return;
    }

    fetch(`${API_BASE_URL}post_comment.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        post_id: POST_ID,
        user_id: currentUserId,
        content: text,
        parent_id: parentId,
      }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("Network response was not ok");
        return r.json();
      })
      .then((res) => {
        if (res.error) {
          setErrorMsg(res.error);
          return;
        }
        
        if (!res.id) {
          setErrorMsg("An unknown error occurred.");
          return;
        }

        fetchComments();

        if (location.search) {
          navigate(location.pathname, { replace: true });
        }
      })
      .catch((err) => {
        console.error("Post Error:", err);
        setErrorMsg("Failed to post comment. Please try again.");
      });
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const processed = await convertMentionsToUserIds(newComment);
      postCommentToBackend(0, processed);
      setNewComment("");
    } catch (e) {
      setErrorMsg("Error processing comment.");
    }
  };

  const handleAddReply = async (parentId, txt) => {
    try {
      const processed = await convertMentionsToUserIds(txt);
      postCommentToBackend(parentId, processed);
    } catch (e) {
      setErrorMsg("Error processing reply.");
    }
  };

  return (
    <div className={styles.commentSection}>
      <h3 className={styles.heading}>Comments</h3>

      {!isClosed && (
        <div className={styles.newCommentRow}>
          <input
            className={styles.inputBox}
            placeholder={
              currentUserId === 0
                ? "Log in to add a comment..."
                : "Add a comment..."
            }
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={currentUserId === 0}
          />

          <button
            className={styles.postBtn}
            onClick={handleAddComment}
            disabled={!newComment.trim() || currentUserId === 0}
          >
            Post
          </button>
        </div>
      )}

      {isClosed && (
        <div className={styles.lockedMessage}>
          Comments are locked on closed posts.
        </div>
      )}

      <div className={styles.commentList}>
        {comments.map((c) => (
          <CommentItem
            key={c.id}
            comment={c}
            depth={0}
            userMap={userMap}
            onAddReply={handleAddReply}
            highlightedCommentId={highlightedCommentId}
            isClosed={isClosed}
          />
        ))}
      </div>

      {errorMsg && (
        <div className={styles.errorContainer}>
          <div className={styles.alertError}>{errorMsg}</div>
        </div>
      )}
    </div>
  );
}